import React, { useState, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import { bool, func, object } from 'prop-types';
import { t } from 'i18next';

import { CORE_SETTINGS_TARGET } from '../../screens/Home/redux/constants';

import { DELIVERY_TARGET } from '~redux/SearchStore/constants';
import { businessHoursDelivery, currentTimestamp, getDaysRange } from '~utils/time';

import {
  AS_SOON_AS_POSSIBLE,
  DEFAULT_TIME_SELECTION,
  MAX_HOURS,
  MINUTES_PER_HOUR,
  SCHEDULE_TIME
} from './constants';
import SelectTimeDispatch from './layout';

function SelectTimeDispatchContainer({
  selectedDeliveryTime,
  deliveryInfo,
  futureOrdersEnabled,
  pickUp,
  selectedStore,
  onSelectedTime,
  setScheduled
}) {
  const [selectedTime, setSelectedTime] = useState(null);
  const { deliveryWaitTime, businessHours, deliveryOpen, pickUpWaitTime, pickUpOpen } = pickUp
    ? selectedStore
    : deliveryInfo;
  const [radioButtonSelected, setRadioButtonSelected] = useState();

  const waitTime = pickUp && selectedStore ? pickUpWaitTime : deliveryWaitTime;

  const changeToAsSoonAsPossible = () =>
    onSelectedTime({
      now: t('Delivery:time', { time: waitTime })
    });

  const handleChangeRadioButton = event => {
    if (event.target.value === AS_SOON_AS_POSSIBLE) {
      setSelectedTime(null);
      changeToAsSoonAsPossible();
      setScheduled(false);
      return;
    }
    setScheduled(true);
    onSelectedTime(null);
    setRadioButtonSelected(event.target.value);
  };

  const daysOptions = useMemo(
    () => getDaysRange(MAX_HOURS, waitTime, businessHoursDelivery(businessHours, waitTime, pickUp)),
    [MAX_HOURS, waitTime, businessHours]
  );

  const dispatchDay = daysOptions[0];

  const onSelectTime = value => {
    if (radioButtonSelected === SCHEDULE_TIME) {
      setSelectedTime(value?.target?.value || value);
    }
  };

  useEffect(() => {
    if (deliveryOpen) {
      setRadioButtonSelected(AS_SOON_AS_POSSIBLE);
      setScheduled(false);
    } else {
      setScheduled(true);
      onSelectedTime(null);
      setRadioButtonSelected(SCHEDULE_TIME);
    }
  }, [deliveryOpen]);

  useEffect(() => {
    if (selectedStore && !pickUpOpen) {
      setSelectedTime(-1);
      onSelectedTime(null);
      setRadioButtonSelected(SCHEDULE_TIME);
      setScheduled(true);
    }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedTime !== null && radioButtonSelected === SCHEDULE_TIME && dispatchDay) {
      const { date, deliveryRanges, text } = dispatchDay;
      const selectedDeliveryRange = deliveryRanges.find(time => time.value === selectedTime);
      const dateSelected = date?.format('YYYY-MM-DDT') + selectedDeliveryRange?.start.format('HH:mm');
      selectedTime < 0
        ? onSelectedTime(null)
        : onSelectedTime({
            day: dispatchDay,
            time: {
              ...selectedDeliveryRange,
              name: `${text}, ${selectedDeliveryRange?.name}`
            },
            date: dateSelected
          });
    }
  }, [selectedTime, dispatchDay]);

  useEffect(() => {
    if (selectedTime === null) {
      if (selectedDeliveryTime && !selectedDeliveryTime.now) {
        setSelectedTime(selectedDeliveryTime.time.value);
        setRadioButtonSelected(SCHEDULE_TIME);
        setScheduled(true);
      } else if ((!pickUp && deliveryOpen) || (pickUp && pickUpOpen)) {
        setSelectedTime(-1);
        setRadioButtonSelected(AS_SOON_AS_POSSIBLE);
        setScheduled(false);
        changeToAsSoonAsPossible();
      }
    }
  }, [selectedDeliveryTime, selectedTime]);

  const getTotalMinutes = hour => {
    const [hours, minutes] = hour.split(':');

    return parseInt(hours) * MINUTES_PER_HOUR + parseInt(minutes);
  };

  const getTimeOptions = () => {
    const currentTime = currentTimestamp();

    const timeOptions = dispatchDay?.deliveryRanges
      .filter(item => {
        if (dispatchDay.text === t('SelectTimeDispatch:today')) {
          const currentMinutesTime = getTotalMinutes(currentTime.format('HH:mm'));
          const minutesStart = getTotalMinutes(item.start.format('HH:mm'));
          const diffInMinutes = minutesStart - currentMinutesTime;

          return diffInMinutes > MINUTES_PER_HOUR;
        }
        return item;
      })
      .map(item => ({ ...item, name: `${dispatchDay.text}, ${item.name}` }));

    return [DEFAULT_TIME_SELECTION, ...timeOptions];
  };

  if (!dispatchDay || !radioButtonSelected) {
    return null;
  }

  const timeRanges = dispatchDay?.deliveryRanges || [];
  const timeOptions = getTimeOptions();

  return (
    <SelectTimeDispatch
      timeOptions={timeOptions}
      onSelectTime={onSelectTime}
      selectedTime={timeRanges.find(time => time.value === selectedTime) || DEFAULT_TIME_SELECTION}
      deliveryOpen={deliveryOpen}
      deliveryWaitTime={deliveryWaitTime}
      radioButtonSelected={radioButtonSelected}
      handleChangeRadioButton={handleChangeRadioButton}
      futureOrdersEnabled={futureOrdersEnabled}
      dispatchDay={dispatchDay}
      pickUp={pickUp}
      pickUpOpen={pickUpOpen}
    />
  );
}

SelectTimeDispatchContainer.propTypes = {
  deliveryInfo: object,
  futureOrdersEnabled: bool,
  pickUp: bool,
  selectedDeliveryTime: object,
  selectedStore: object,
  setScheduled: func,
  onSelectedTime: func
};

const mapStateToProps = state => ({
  deliveryInfo: state.searchStore[DELIVERY_TARGET],
  futureOrdersEnabled: state.home[CORE_SETTINGS_TARGET]?.futureOrdersEnabled
});

export default connect(mapStateToProps)(SelectTimeDispatchContainer);
